/**
 * ====================================================================
 * FIKO CAD FALLBACK RESOLVER
 * ====================================================================
 *
 * Çizilemez durumlar için çözümler sunar.
 * Her edge case için mantıklı bir fallback sağlar.
 *
 * Durumlar:
 * - Çakışık noktalar (coincident points)
 * - Paralel çizgiler (angular için)
 * - Sıfır yarıçap
 * - Ekran dışı
 * - Geometri tarafından kapatılma
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import {
    Dimension,
    DimensionType,
    DimensionErrorType,
    GeometryReference,
    FallbackInfo,
    DIMENSION_CONSTANTS
} from './DimensionTypes';
import type { DimensionManager } from './DimensionManager';

/**
 * Fallback stratejisi
 */
export enum FallbackStrategy {
    SKIP = 'SKIP',                      // Ölçümü atla
    MINIMUM_VALUE = 'MINIMUM_VALUE',    // Minimum değer göster
    ALTERNATIVE_PLACEMENT = 'ALTERNATIVE_PLACEMENT',  // Alternatif yerleşim
    LEADER_LINE = 'LEADER_LINE',        // Leader line ekle
    ANNOTATION = 'ANNOTATION',          // Sadece yazı göster
    WARNING = 'WARNING'                 // Uyarı ile göster
}

/**
 * Fallback sonucu
 */
export interface FallbackResult {
    strategy: FallbackStrategy;
    applied: boolean;
    message: string;
    modifiedValue?: number;
    modifiedPlacement?: any;
}

/**
 * Fallback Resolver - Çözümleyici
 */
export class FallbackResolver {
    // Manager referansı
    private manager: DimensionManager;

    // Strateji öncelikleri
    private readonly strategyPriority: Map<DimensionErrorType, FallbackStrategy[]> = new Map([
        [DimensionErrorType.COINCIDENT_POINTS, [
            FallbackStrategy.MINIMUM_VALUE,
            FallbackStrategy.ANNOTATION,
            FallbackStrategy.SKIP
        ]],
        [DimensionErrorType.PARALLEL_LINES, [
            FallbackStrategy.WARNING,
            FallbackStrategy.ANNOTATION,
            FallbackStrategy.SKIP
        ]],
        [DimensionErrorType.NO_INTERSECTION, [
            FallbackStrategy.ALTERNATIVE_PLACEMENT,
            FallbackStrategy.LEADER_LINE,
            FallbackStrategy.SKIP
        ]],
        [DimensionErrorType.OCCLUSION, [
            FallbackStrategy.LEADER_LINE,
            FallbackStrategy.ALTERNATIVE_PLACEMENT,
            FallbackStrategy.WARNING
        ]],
        [DimensionErrorType.OUT_OF_BOUNDS, [
            FallbackStrategy.ALTERNATIVE_PLACEMENT,
            FallbackStrategy.WARNING,
            FallbackStrategy.SKIP
        ]],
        [DimensionErrorType.INVALID_REFERENCE, [
            FallbackStrategy.WARNING,
            FallbackStrategy.SKIP
        ]],
        [DimensionErrorType.INTERNAL_ERROR, [
            FallbackStrategy.SKIP
        ]]
    ]);

    constructor(manager: DimensionManager) {
        this.manager = manager;
    }

    /**
     * Hata tipine göre fallback uygula
     */
    resolve(
        errorType: DimensionErrorType,
        dimension?: Dimension,
        context?: any
    ): FallbackResult {
        const strategies = this.strategyPriority.get(errorType) || [FallbackStrategy.SKIP];

        for (const strategy of strategies) {
            const result = this.applyStrategy(strategy, errorType, dimension, context);
            if (result.applied) {
                return result;
            }
        }

        // Hiçbir strateji başarılı olmadı
        return {
            strategy: FallbackStrategy.SKIP,
            applied: true,
            message: 'Ölçüm oluşturulamadı, atlandı'
        };
    }

    /**
     * Strateji uygula
     */
    private applyStrategy(
        strategy: FallbackStrategy,
        errorType: DimensionErrorType,
        dimension?: Dimension,
        context?: any
    ): FallbackResult {
        switch (strategy) {
            case FallbackStrategy.MINIMUM_VALUE:
                return this.applyMinimumValue(errorType, context);

            case FallbackStrategy.ALTERNATIVE_PLACEMENT:
                return this.applyAlternativePlacement(dimension, context);

            case FallbackStrategy.LEADER_LINE:
                return this.applyLeaderLine(dimension, context);

            case FallbackStrategy.ANNOTATION:
                return this.applyAnnotation(dimension, context);

            case FallbackStrategy.WARNING:
                return this.applyWarning(errorType, context);

            case FallbackStrategy.SKIP:
            default:
                return {
                    strategy: FallbackStrategy.SKIP,
                    applied: true,
                    message: this.getSkipMessage(errorType)
                };
        }
    }

    /**
     * Minimum değer stratejisi - Çok küçük değerler için
     */
    private applyMinimumValue(
        errorType: DimensionErrorType,
        context?: any
    ): FallbackResult {
        const minDistance = DIMENSION_CONSTANTS.MIN_DISTANCE;

        return {
            strategy: FallbackStrategy.MINIMUM_VALUE,
            applied: true,
            message: `Minimum mesafe uygulandı: ${minDistance}mm`,
            modifiedValue: minDistance
        };
    }

    /**
     * Alternatif yerleşim stratejisi
     */
    private applyAlternativePlacement(
        dimension?: Dimension,
        context?: any
    ): FallbackResult {
        if (!dimension) {
            return {
                strategy: FallbackStrategy.ALTERNATIVE_PLACEMENT,
                applied: false,
                message: 'Dimension bulunamadı'
            };
        }

        // Layout manager'dan öneriler al
        const layoutManager = this.manager.getLayoutManager();
        const suggestions = layoutManager.suggestPlacements(dimension);

        if (suggestions.length > 0) {
            const best = suggestions[0];
            return {
                strategy: FallbackStrategy.ALTERNATIVE_PLACEMENT,
                applied: true,
                message: `Alternatif yerleşim uygulandı: offset=${best.offset}`,
                modifiedPlacement: best
            };
        }

        return {
            strategy: FallbackStrategy.ALTERNATIVE_PLACEMENT,
            applied: false,
            message: 'Uygun alternatif yerleşim bulunamadı'
        };
    }

    /**
     * Leader line stratejisi - Kapatılan dimension için
     */
    private applyLeaderLine(
        dimension?: Dimension,
        context?: any
    ): FallbackResult {
        if (!dimension) {
            return {
                strategy: FallbackStrategy.LEADER_LINE,
                applied: false,
                message: 'Dimension bulunamadı'
            };
        }

        // Leader line ekle (dimension tipine göre)
        // Bu gerçek implementasyonda dimension'a leader eklenir
        return {
            strategy: FallbackStrategy.LEADER_LINE,
            applied: true,
            message: 'Leader line eklendi'
        };
    }

    /**
     * Annotation stratejisi - Sadece yazı göster
     */
    private applyAnnotation(
        dimension?: Dimension,
        context?: any
    ): FallbackResult {
        return {
            strategy: FallbackStrategy.ANNOTATION,
            applied: true,
            message: 'Sadece annotation olarak gösteriliyor'
        };
    }

    /**
     * Uyarı stratejisi
     */
    private applyWarning(
        errorType: DimensionErrorType,
        context?: any
    ): FallbackResult {
        const warningMessages: Record<DimensionErrorType, string> = {
            [DimensionErrorType.COINCIDENT_POINTS]: 'Noktalar çakışık, ölçüm gösterilemiyor',
            [DimensionErrorType.PARALLEL_LINES]: 'Çizgiler paralel, açı ölçülemiyor',
            [DimensionErrorType.NO_INTERSECTION]: 'Kesişim noktası bulunamadı',
            [DimensionErrorType.OCCLUSION]: 'Ölçüm geometri tarafından kapatılıyor',
            [DimensionErrorType.OUT_OF_BOUNDS]: 'Ölçüm ekran sınırları dışında',
            [DimensionErrorType.INVALID_REFERENCE]: 'Geçersiz referans',
            [DimensionErrorType.INTERNAL_ERROR]: 'Dahili hata oluştu'
        };

        return {
            strategy: FallbackStrategy.WARNING,
            applied: true,
            message: warningMessages[errorType] || 'Bilinmeyen hata'
        };
    }

    /**
     * Skip mesajı al
     */
    private getSkipMessage(errorType: DimensionErrorType): string {
        const messages: Record<DimensionErrorType, string> = {
            [DimensionErrorType.COINCIDENT_POINTS]: 'Çakışık noktalar, ölçüm atlandı',
            [DimensionErrorType.PARALLEL_LINES]: 'Paralel çizgiler, açı ölçümü atlandı',
            [DimensionErrorType.NO_INTERSECTION]: 'Kesişim yok, ölçüm atlandı',
            [DimensionErrorType.OCCLUSION]: 'Kapatılmış, ölçüm atlandı',
            [DimensionErrorType.OUT_OF_BOUNDS]: 'Sınır dışı, ölçüm atlandı',
            [DimensionErrorType.INVALID_REFERENCE]: 'Geçersiz referans, ölçüm atlandı',
            [DimensionErrorType.INTERNAL_ERROR]: 'Hata, ölçüm atlandı'
        };

        return messages[errorType] || 'Ölçüm atlandı';
    }

    /**
     * Referans geçerliliğini kontrol et
     */
    validateReference(ref: GeometryReference): { valid: boolean; error?: DimensionErrorType } {
        if (!ref) {
            return { valid: false, error: DimensionErrorType.INVALID_REFERENCE };
        }

        // Nokta kontrolü
        if (ref.point) {
            if (!this.isValidVector(ref.point)) {
                return { valid: false, error: DimensionErrorType.INVALID_REFERENCE };
            }
        }

        // Merkez kontrolü
        if (ref.center) {
            if (!this.isValidVector(ref.center)) {
                return { valid: false, error: DimensionErrorType.INVALID_REFERENCE };
            }
        }

        // Yarıçap kontrolü
        if (ref.radius !== undefined) {
            if (!isFinite(ref.radius) || ref.radius < 0) {
                return { valid: false, error: DimensionErrorType.INVALID_REFERENCE };
            }
        }

        return { valid: true };
    }

    /**
     * İki referansın çakışık olup olmadığını kontrol et
     */
    checkCoincident(refA: GeometryReference, refB: GeometryReference): boolean {
        const pointA = refA.point || refA.center;
        const pointB = refB.point || refB.center;

        if (!pointA || !pointB) {
            return false;
        }

        const distance = pointA.distanceTo(pointB);
        return distance < DIMENSION_CONSTANTS.MIN_DISTANCE;
    }

    /**
     * İki yönün paralel olup olmadığını kontrol et
     */
    checkParallel(dirA: THREE.Vector3, dirB: THREE.Vector3): boolean {
        const dot = Math.abs(dirA.dot(dirB));
        return dot > 0.9999; // Neredeyse paralel
    }

    /**
     * Vektör geçerli mi?
     */
    private isValidVector(v: THREE.Vector3): boolean {
        return isFinite(v.x) && isFinite(v.y) && isFinite(v.z);
    }

    /**
     * Temizlik
     */
    dispose(): void {
        // Temizlenecek kaynak yok
    }
}
